import { PaymentBatchRecipient } from "@aidkitorg/aidkit/lib/admin_panel/payments"
import { Button, Combobox, Select } from "@aidkitorg/component-library"
import { useReactTable } from "@tanstack/react-table"
import React from "react"
import { useLocalizedStrings } from "../../../Localization"

const PAYMENT_METHOD_OPTIONS = [
  {value: 'ACH', label: 'ACH'},
  {value: 'USIO Mailed Card', label: 'USIO Mailed Card'},
  {value: 'USIO Card Load', label: 'USIO Card Load'},
  {value: 'Western Union Grant', label: 'Western Union Grant'},
  {value: 'Screener Payment', label: 'Screener Payment'},
  {value: 'Givecard (mail)', label: 'Givecard (mail)'},
  {value: 'Givecard (RTP)', label: 'Givecard (RTP)'},
  {value: 'US Bank Mailed Card', label: 'US Bank Mailed Card'},
  {value: 'Givecard Virtual Card', label: 'Givecard Virtual Card'},
  {value: 'Givecard Card Load', label: 'Givecard Card Load'}
]

export type PaymentBatchRecipientFilterProps = {
  table: ReturnType<typeof useReactTable<PaymentBatchRecipient>>,
  setActiveFilters: React.Dispatch<React.SetStateAction<number>>,
  filters: PaymentBatchRecipientFilters,
  setFilters: React.Dispatch<React.SetStateAction<PaymentBatchRecipientFilters>>,
  availablePaymentMethods: Set<string>
}

export type PaymentBatchRecipientFilters = {
  paymentStatus: Array<{label: string, value: string}>
  paymentMethod: string,
  paymentInitiatedDateStart: string,
  paymentInitiatedDateEnd: string,
}

export const PaymentBatchRecipientFilter: React.FC<PaymentBatchRecipientFilterProps> = ({
  table,
  setActiveFilters,
  filters,
  setFilters,
  availablePaymentMethods
}) => {
  const L = useLocalizedStrings()

  const applyFilters = () => {
    let activeFilters = 0;

    if (filters.paymentStatus.length !== 0) activeFilters++;
    const paymentStatusCol = table.getColumn('paymentStatus')
    paymentStatusCol?.setFilterValue(filters.paymentStatus || undefined)

    if (filters.paymentMethod !== "") activeFilters++;
    const paymentMethodCol = table.getColumn('paymentMethod')
    paymentMethodCol?.setFilterValue(filters.paymentMethod || undefined)

    if (filters.paymentInitiatedDateStart !== "" || filters.paymentInitiatedDateEnd !== "") activeFilters++
    const paymentInitiatedDateCol = table.getColumn('paymentInitiatedDate')
    paymentInitiatedDateCol?.setFilterValue(
      filters.paymentInitiatedDateStart || filters.paymentInitiatedDateEnd
        ? { paymentInitiatedDateStart: filters.paymentInitiatedDateStart, paymentInitiatedDateEnd: filters.paymentInitiatedDateEnd }
        : undefined
    )

    setActiveFilters(activeFilters)
  }

  const resetFilters = () => {
    setFilters({
      paymentStatus: [],
      paymentMethod: '',
      paymentInitiatedDateStart: '',
      paymentInitiatedDateEnd: ''
    })
  }

  const PAYMENT_STATUS_FILTER_OPTIONS = [
    {value: 'eligible', label: L.admin_panel.payments.eligible},
    {value: 'pending', label: L.admin_panel.payments.pending},
    {value: 'scheduled', label: L.admin_panel.payments.scheduled},
    {value: 'paid', label: L.admin_panel.payments.paid},
    {value: 'failed', label: L.admin_panel.payments.failed},
    {value: 'paused', label: L.admin_panel.payments.paused},
  ]  

  const FILTERED_PAYMENT_METHODS = PAYMENT_METHOD_OPTIONS.filter((paymentMethod) => availablePaymentMethods.has(paymentMethod.value))

  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-4">
        <div>
          <Combobox 
            className="h-10"
            by="value"
            label={L.admin_panel.payments.payment_status}
            multiple={true}
            options={PAYMENT_STATUS_FILTER_OPTIONS}
            value={filters.paymentStatus}
            onChange={(value) => setFilters(f => ({...f, paymentStatus: value}))}
          />
        </div>
        <div className="min-w-56">
          <Select 
            className="w-full max-h-10"
            label={L.admin_panel.payments.payment_method}
            description={L.admin_panel.payments.payment_method_desc}
            labelLocation="above"
            anchor="bottom start"
            options={FILTERED_PAYMENT_METHODS}
            value={FILTERED_PAYMENT_METHODS.find((v) => v.value == filters.paymentMethod)}
            onChange={(value) => setFilters(f => ({...f, paymentMethod: value.value}))}
          />
        </div>
        <div>
          <label className="block text-sm/6 font-medium text-gray-900 mb-1">{L.admin_panel.payments.payment_initiated}</label>
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label={L.admin_panel.payments.payment_initiated_from}
              type="date"
              value={filters.paymentInitiatedDateStart}
              onChange={(e) => setFilters(f => ({...f, paymentInitiatedDateStart: e.target.value}))}
              className="min-height-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
            />
            <span className="text-gray-700">to</span>
            <input
              aria-label={L.admin_panel.payments.payment_initiated_to}
              type="date"
              value={filters.paymentInitiatedDateEnd}
              onChange={(e) => setFilters(f => ({...f, paymentInitiatedDateEnd: e.target.value}))}
              className="min-h-10 border px-3 py-2 rounded-md text-sm shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:offset-1 focus-visible:outline-indigo-600"
            />
          </div>
        </div>
      </div>

      <div className="pt-4">
        <div className="flex gap-2">
          <Button onClick={resetFilters} variant="secondary">{L.admin_panel.payments.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.payments.apply_filters}</Button>
        </div>
      </div> 
    </div>
  )
}